/**
 * Session Slice
 *
 * Manages session state for the simplified login system:
 * - User type (teacher or student)
 * - Animal password code (synced with Firestore)
 * - Device tracking (via Firestore)
 * - Session expiry
 */

import type { StateCreator } from 'zustand';
import type { AnimalType } from '@/components/AnimalPassword';
import { generateAnimalCode, generateDeviceId, calculateSessionExpiry } from '@/components/AnimalPassword';
import { sessionsService } from '@/lib/firebase/firestore';

/**
 * User type for simplified login
 */
export type SessionUserType = 'teacher' | 'student' | null;

/**
 * Session state interface
 */
export interface SessionState {
  /** Current user type */
  userType: SessionUserType;
  /** Current teacher ID (Firebase UID) - needed for Firestore operations */
  teacherId: string | null;
  /** Current animal password code (synced with Firestore) */
  animalCode: AnimalType[] | null;
  /** Student's selected number (0-30) - for student identification */
  studentNumber: number | null;
  /** Unique device identifier */
  deviceId: string;
  /** Session expiry timestamp (ms since epoch) */
  sessionExpiry: number | null;
  /** Number of active devices (from Firestore) */
  deviceCount: number;
  /** Maximum allowed devices */
  maxDevices: number;
  /** Last teacher login timestamp */
  lastTeacherLogin: number | null;
  /** Whether student login is available (teacher has active session) */
  studentLoginEnabled: boolean;
  /** Loading state for async operations */
  sessionLoading: boolean;
  /** Error state for async operations */
  sessionError: string | null;
  /** Currently cast app ID (teacher broadcasts to students) */
  castAppId: string | null;
  /** Timestamp when cast started */
  castStartedAt: number | null;
}

/**
 * Session actions interface
 */
export interface SessionActions {
  /** Set the current user type */
  setUserType: (userType: SessionUserType) => void;
  /** Set the teacher ID */
  setTeacherId: (teacherId: string | null) => void;
  /** Set the animal code (local only) */
  setAnimalCode: (code: AnimalType[] | null) => void;
  /** Generate a new random animal code (local only, for testing) */
  generateNewAnimalCode: () => void;
  /** Set the student number (0-30) for student identification */
  setStudentNumber: (num: number | null) => void;
  /** Set the device count */
  setDeviceCount: (count: number) => void;
  /** Enable/disable student login */
  setStudentLoginEnabled: (enabled: boolean) => void;
  /** Set session loading state */
  setSessionLoading: (loading: boolean) => void;
  /** Set session error */
  setSessionError: (error: string | null) => void;

  // ============================================================================
  // Firestore-integrated actions (async)
  // ============================================================================

  /**
   * Login as teacher with Firestore sync.
   * Creates or retrieves session from Firestore, registers device.
   */
  teacherLoginWithFirestore: (firebaseUid: string) => Promise<void>;

  /**
   * Login as student by validating animal code against Firestore.
   * Finds the teacher session by animal code and registers device.
   */
  studentLoginWithFirestore: (enteredCode: AnimalType[]) => Promise<boolean>;

  /**
   * Generate and save new animal code to Firestore.
   * Only works for teachers.
   */
  regenerateAnimalCodeWithFirestore: () => Promise<void>;

  /**
   * Logout with Firestore sync.
   * Unregisters device from Firestore.
   */
  logoutWithFirestore: () => Promise<void>;

  // ============================================================================
  // Cast actions (teacher broadcasts app to students)
  // ============================================================================

  /**
   * Start casting an app to students.
   * Only one app can be cast at a time.
   */
  startCast: (appId: string) => Promise<void>;

  /**
   * Stop casting (students see waiting screen).
   */
  stopCast: () => Promise<void>;

  /**
   * Internal: Set cast state from Firestore subscription.
   */
  setCastState: (appId: string | null, startedAt: number | null) => void;

  // ============================================================================
  // Legacy actions (local only, for backwards compatibility)
  // ============================================================================

  /** Login as teacher (local only) */
  teacherLogin: () => void;
  /** Login as student (local only) */
  studentLogin: () => boolean;
  /** Logout current session (local only) */
  sessionLogout: () => void;
  /** Check if session is still valid */
  isSessionValid: () => boolean;
  /** Refresh session expiry */
  refreshSession: () => void;
  /** Set last teacher login timestamp */
  setLastTeacherLogin: (timestamp: number | null) => void;
}

/**
 * Combined session slice type
 */
export type SessionSlice = SessionState & SessionActions;

/**
 * Default initial state
 */
const initialSessionState: SessionState = {
  userType: null,
  teacherId: null,
  animalCode: null,
  studentNumber: null,
  deviceId: typeof window !== 'undefined' ? generateDeviceId() : 'server',
  sessionExpiry: null,
  deviceCount: 0,
  maxDevices: 30,
  lastTeacherLogin: null,
  studentLoginEnabled: false,
  sessionLoading: false,
  sessionError: null,
  castAppId: null,
  castStartedAt: null,
};

/**
 * Create the session slice
 */
export const createSessionSlice: StateCreator<
  SessionSlice,
  [],
  [],
  SessionSlice
> = (set, get) => ({
  ...initialSessionState,

  // Basic setters
  setUserType: (userType) => set({ userType }),
  setTeacherId: (teacherId) => set({ teacherId }),
  setAnimalCode: (code) => set({ animalCode: code }),
  setStudentNumber: (num) => set({ studentNumber: num }),
  setDeviceCount: (count) => set({ deviceCount: count }),
  setStudentLoginEnabled: (enabled) => set({ studentLoginEnabled: enabled }),
  setSessionLoading: (loading) => set({ sessionLoading: loading }),
  setSessionError: (error) => set({ sessionError: error }),
  setLastTeacherLogin: (timestamp) => set({ lastTeacherLogin: timestamp }),

  generateNewAnimalCode: () => {
    const newCode = generateAnimalCode();
    set({
      animalCode: newCode,
      studentLoginEnabled: true,
    });
  },

  // ============================================================================
  // Firestore-integrated actions
  // ============================================================================

  teacherLoginWithFirestore: async (firebaseUid: string) => {
    const state = get();
    set({ sessionLoading: true, sessionError: null });

    try {
      // Get or create session in Firestore
      const { animalCode, deviceCount, isNew } = await sessionsService.getOrCreateSession(
        firebaseUid,
        generateAnimalCode
      );

      // Register this device
      await sessionsService.registerDevice(firebaseUid, state.deviceId, 'teacher');

      // Update local state
      set({
        userType: 'teacher',
        teacherId: firebaseUid,
        animalCode,
        deviceCount,
        sessionExpiry: calculateSessionExpiry(),
        lastTeacherLogin: Date.now(),
        studentLoginEnabled: true,
        sessionLoading: false,
        sessionError: null,
      });

      if (isNew) {
        console.log('Created new session with fresh animal code');
      } else {
        console.log('Restored existing session');
      }
    } catch (error) {
      console.error('Teacher login error:', error);
      set({
        sessionLoading: false,
        sessionError: error instanceof Error ? error.message : 'Failed to login',
      });
      throw error;
    }
  },

  studentLoginWithFirestore: async (enteredCode: AnimalType[]) => {
    const state = get();
    set({ sessionLoading: true, sessionError: null });

    try {
      // Find teacher by animal code
      const teacherId = await sessionsService.findTeacherByAnimalCode(enteredCode);

      if (!teacherId) {
        set({
          sessionLoading: false,
          sessionError: 'Invalid code or no active session',
        });
        return false;
      }

      // Try to register device
      const { success, deviceCount } = await sessionsService.registerDevice(
        teacherId,
        state.deviceId,
        'student'
      );

      if (!success) {
        set({
          sessionLoading: false,
          sessionError: 'Classroom is full. Please wait for a spot to open.',
        });
        return false;
      }

      // Update local state
      set({
        userType: 'student',
        teacherId,
        animalCode: enteredCode,
        deviceCount,
        sessionExpiry: calculateSessionExpiry(),
        studentLoginEnabled: true,
        sessionLoading: false,
        sessionError: null,
      });

      return true;
    } catch (error) {
      console.error('Student login error:', error);
      set({
        sessionLoading: false,
        sessionError: error instanceof Error ? error.message : 'Failed to login',
      });
      return false;
    }
  },

  regenerateAnimalCodeWithFirestore: async () => {
    const state = get();

    if (!state.teacherId || state.userType !== 'teacher') {
      console.error('Only teachers can regenerate animal code');
      return;
    }

    set({ sessionLoading: true, sessionError: null });

    try {
      const newCode = await sessionsService.regenerateAnimalCode(
        state.teacherId,
        generateAnimalCode
      );

      set({
        animalCode: newCode,
        sessionLoading: false,
        sessionError: null,
      });
    } catch (error) {
      console.error('Regenerate code error:', error);
      set({
        sessionLoading: false,
        sessionError: error instanceof Error ? error.message : 'Failed to generate new code',
      });
      throw error;
    }
  },

  logoutWithFirestore: async () => {
    const state = get();

    try {
      // Unregister device from Firestore if we have a teacher ID
      if (state.teacherId) {
        await sessionsService.unregisterDevice(state.teacherId, state.deviceId);
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local logout even if Firestore fails
    }

    // Clear local state
    set({
      userType: null,
      teacherId: null,
      studentNumber: null,
      sessionExpiry: null,
      sessionError: null,
    });
  },

  // ============================================================================
  // Cast actions
  // ============================================================================

  startCast: async (appId: string) => {
    const state = get();

    if (!state.teacherId || state.userType !== 'teacher') {
      console.error('Only teachers can start casting');
      return;
    }

    try {
      await sessionsService.startCast(state.teacherId, appId, state.deviceId);
      set({
        castAppId: appId,
        castStartedAt: Date.now(),
      });
    } catch (error) {
      console.error('Start cast error:', error);
      throw error;
    }
  },

  stopCast: async () => {
    const state = get();

    if (!state.teacherId || state.userType !== 'teacher') {
      console.error('Only teachers can stop casting');
      return;
    }

    try {
      await sessionsService.stopCast(state.teacherId);
      set({
        castAppId: null,
        castStartedAt: null,
      });
    } catch (error) {
      console.error('Stop cast error:', error);
      throw error;
    }
  },

  setCastState: (appId: string | null, startedAt: number | null) => {
    set({
      castAppId: appId,
      castStartedAt: startedAt,
    });
  },

  // ============================================================================
  // Legacy actions (local only)
  // ============================================================================

  teacherLogin: () => {
    const now = Date.now();
    const state = get();

    // Check if we need to generate a new code (first login or >24h since last)
    const needNewCode = !state.lastTeacherLogin ||
      (now - state.lastTeacherLogin) > 24 * 60 * 60 * 1000;

    set({
      userType: 'teacher',
      sessionExpiry: calculateSessionExpiry(),
      lastTeacherLogin: now,
      // Generate new code if needed
      animalCode: needNewCode ? generateAnimalCode() : state.animalCode,
      studentLoginEnabled: true,
    });
  },

  studentLogin: () => {
    const state = get();

    // Check if student login is enabled
    if (!state.studentLoginEnabled) return false;

    // Check device limit
    if (state.deviceCount >= state.maxDevices) return false;

    set({
      userType: 'student',
      sessionExpiry: calculateSessionExpiry(),
    });
    return true;
  },

  sessionLogout: () =>
    set({
      userType: null,
      teacherId: null,
      studentNumber: null,
      sessionExpiry: null,
    }),

  isSessionValid: () => {
    const state = get();
    if (!state.sessionExpiry) return false;
    return Date.now() < state.sessionExpiry;
  },

  refreshSession: () => {
    const state = get();
    if (state.userType) {
      set({ sessionExpiry: calculateSessionExpiry() });
    }
  },
});

/**
 * Default export
 */
export default createSessionSlice;
